import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { FieldGroup, FormArray, FormBuilder } from "react-reactive-form";
import ComparativePDQuestions from "../../questions/ComparativePDQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import { populateValueHelperArray } from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";

const isProduction = process.env.NODE_ENV === "production";

const ComparativePharmacodynamics = () => {
  const { studyId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
    updatePdTreatmentGroupsComparisonAdditionalData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacySensitivityNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacySensitivityNames: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    if (!questionsDataSaved) {
      setAjaxLoading(true);
      CsrAPI.getQuestions({ studyId })
        .then(questionsDataSaved => {
          replaceNullArrays(questionsDataSaved);
          setQuestionsDataSaved(questionsDataSaved);
          setErrMsg("");
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else if (error.response?.status === 403) {
            setShowRedirect(true);
          } else {
            setErrMsg("Failed to get questions");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    }
  }, [studyId, setQuestionsDataSaved, questionsDataSaved]);

  const replaceNullArrays = obj => {
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        if (Array.isArray(obj[key])) {
          if (obj[key].every(value => value === null)) {
            obj[key] = [];
          }
        } else {
          replaceNullArrays(obj[key]);
        }
      }
    }
  };

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      const wizardFormValue = questionsDataSaved.wizardForm;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (formArraysKeys[key] && wizardForm.controls[key]) {
          while (wizardForm.controls[key].length > 0) {
            wizardForm.controls[key].removeAt(0);
          }
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        if (formArraysKeys[key]) {
          if (formArraysKeys[key] === "object") {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key]?.push(FormBuilder.group(item));
            });
          } else {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key].push(FormBuilder.control(item));
            });
          }
        } else {
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Extract qPdCompEnabled from root (new format) or inside qPdCompAnalyses (legacy)
      const compPdEnabled =
        wizardFormValue.qPdCompEnabled ??
        wizardFormValue.qPdCompAnalyses?.qPdCompEnabled ??
        null;
      if (!wizardForm.contains("qPdCompEnabled")) {
        wizardForm.addControl(
          "qPdCompEnabled",
          FormBuilder.control(compPdEnabled)
        );
      } else {
        wizardForm.patchValue({ qPdCompEnabled: compPdEnabled });
      }

      // Ensure qComparative control exists
      if (!wizardForm.contains("qComparative")) {
        wizardForm.addControl(
          "qComparative",
          FormBuilder.control(wizardFormValue.qComparative || "false")
        );
      }

      // Restore Comparative PD blocks from qPdCompAnalyses (flat array or legacy object with .blocks).
      // Fall back to legacy qPdAnalyses.
      const compPdData = wizardFormValue.qPdCompAnalyses;
      const compPdBlocks = Array.isArray(compPdData)
        ? compPdData
        : (compPdData?.blocks ??
          (Array.isArray(wizardFormValue.qPdAnalyses)
            ? wizardFormValue.qPdAnalyses
            : []));
      if (compPdBlocks.length > 0) {
        compPdBlocks.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";

          if (block.qPdTable !== undefined && block.qPdTable !== null) {
            const controlName = `qPdTableComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdTable);
          }

          if (
            block.qPdPopulation !== undefined &&
            block.qPdPopulation !== null
          ) {
            const controlName = `qPdPopulationComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdPopulation);
          }

          if (
            block.qPdComparative !== undefined &&
            block.qPdComparative !== null
          ) {
            const controlName = `qPdComparativeComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPdComparative));
          }

          if (block.qPdCompName !== undefined && block.qPdCompName !== null) {
            const controlName = `qPdCompNameComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdCompName);
          }

          if (
            block.qPdSummaryType !== undefined &&
            block.qPdSummaryType !== null
          ) {
            const controlName = `qPdSummaryTypeComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm.get(controlName).setValue(String(block.qPdSummaryType));
          }

          populateValueHelperArray(
            wizardForm,
            `qPdParametersComp${suffix}`,
            block.qPdParameters
          );

          populateValueHelperArray(
            wizardForm,
            `qPdTreatmentGroupsComp${suffix}`,
            block.qPdTreatmentGroups
          );

          const qPdComparisonPairsControlName = `qPdComparisonPairsComp${suffix}`;
          if (
            block.qPdComparisonPairs &&
            Array.isArray(block.qPdComparisonPairs) &&
            block.qPdComparisonPairs.length > 0
          ) {
            if (!wizardForm.contains(qPdComparisonPairsControlName)) {
              wizardForm.addControl(
                qPdComparisonPairsControlName,
                new FormArray([])
              );
            }
            const comparisonPairsArray = wizardForm.get(
              qPdComparisonPairsControlName
            );
            while (comparisonPairsArray.length > 0) {
              comparisonPairsArray.removeAt(0);
            }
            block.qPdComparisonPairs.forEach(pair => {
              comparisonPairsArray.push(
                FormBuilder.group({
                  Treatment: [pair.Treatment || ""],
                  Comparator: [pair.Comparator || ""],
                })
              );
            });
          }

          // Restore qPdTimepoints array (Comparative PD specific)
          populateValueHelperArray(
            wizardForm,
            `qPdTimepointsComp${suffix}`,
            block.qPdTimepoints
          );

          if (
            block.qPdDoseInteraction !== undefined &&
            block.qPdDoseInteraction !== null
          ) {
            const controlName = `qPdDoseInteractionComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control("false"));
            }
            wizardForm
              .get(controlName)
              .setValue(String(block.qPdDoseInteraction));
          }

          if (
            block.qPdStatTables !== undefined &&
            block.qPdStatTables !== null
          ) {
            const controlName = `qPdStatTablesComp${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            const statTablesValue =
              Array.isArray(block.qPdStatTables) &&
              block.qPdStatTables.length > 0
                ? block.qPdStatTables
                : typeof block.qPdStatTables === "string"
                  ? block.qPdStatTables
                  : "";
            wizardForm.get(controlName).setValue(statTablesValue);
          }
        });
      }

      setRestoreVersion(v => v + 1);
    }
  };

  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setShowRedirect,
    setIsSaving,
    questionsRef: null,
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <ComparativePDQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      questionsData={questionsData.current}
                      updatePdTreatmentGroupsComparisonAdditionalData={
                        updatePdTreatmentGroupsComparisonAdditionalData
                      }
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Answer the following questions related to Comparative Pharmacodynamics"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Standard PD",
        buttonGoToAction: "/csr/" + studyId + "/stdpharmacodynamics",
      }}
      nextButton={{
        buttonTitle: "Data Mapping",
        buttonGoToAction: "/csr/" + studyId + "/data-mapping",
      }}
      saveButtonHandler={handleSubmit}
      saveButton={true}
    />
  );
};

export default ComparativePharmacodynamics;
